import type { TestSetLabelCount } from "services/TestLLMClassifierService";

export type SyntheticDataRow = {
    result: string;
    humanLabels: number;
    syntheticCalls: number;
    total: number;
};

/** Above this many synthetic calls per human label, generated calls start repeating their seeds. */
export const HighUpsamplingRatio = 10;

/**
 * Human labels per result. Answer options that share the same text are combined.
 */
function getHumanLabelsByResult(counts: TestSetLabelCount[]) {
    const humanLabelsByResult = new Map<string, number>();
    for (const { value, count } of counts) {
        humanLabelsByResult.set(
            value,
            (humanLabelsByResult.get(value) ?? 0) + count,
        );
    }
    return humanLabelsByResult;
}

function toRows(
    counts: TestSetLabelCount[],
    syntheticCallsFor: (result: string, humanLabels: number) => number,
): SyntheticDataRow[] {
    return [...getHumanLabelsByResult(counts)].map(([result, humanLabels]) => {
        // Synthetic calls are written from real labeled calls, so a result with none gets none.
        const syntheticCalls =
            humanLabels === 0 ? 0 : syntheticCallsFor(result, humanLabels);
        return {
            result,
            humanLabels,
            syntheticCalls,
            total: humanLabels + syntheticCalls,
        };
    });
}

/**
 * One row per result, with synthetic calls written at `callsPerLabel` per human label.
 */
export function getCallsPerLabelRows(
    counts: TestSetLabelCount[],
    callsPerLabel: number,
): SyntheticDataRow[] {
    return toRows(counts, (_, humanLabels) => humanLabels * callsPerLabel);
}

/**
 * One row per result. With `balanceTo`, each result is topped up to that total;
 * otherwise each uses its chosen number of synthetic calls, or 0.
 */
export function getPerOutcomeRows(
    counts: TestSetLabelCount[],
    options: {
        balanceTo?: number;
        syntheticCallsByResult: ReadonlyMap<string, number>;
    },
): SyntheticDataRow[] {
    return toRows(counts, (result, humanLabels) =>
        options.balanceTo === undefined
            ? options.syntheticCallsByResult.get(result) ?? 0
            : Math.max(options.balanceTo - humanLabels, 0),
    );
}

/** Rows asking for more than `HighUpsamplingRatio` synthetic calls per human label. */
export function getHighUpsamplingRows(rows: SyntheticDataRow[]) {
    return rows.filter(
        (row) =>
            row.humanLabels > 0 &&
            row.syntheticCalls > row.humanLabels * HighUpsamplingRatio,
    );
}

const inputTokensPerCall = 4400;
const outputTokensPerCall = 2400;

/** Rough tokens to write the calls, from typical input and output per call. */
export function getTokenEstimateText(syntheticCalls: number) {
    const tokens = syntheticCalls * (inputTokensPerCall + outputTokensPerCall);
    const formatted = new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(tokens);
    return `~${formatted} tokens`;
}
